/**
 * RST-style die-cut guides derived from the template's own embedded layers.
 *
 * AAV9 labels compute their guides from a physical spec (rectangles + a corner
 * radius). RST labels (folded cartons / stand-up pouches) have shapes far too
 * complex to describe that way — the safe area, bleed, fold, and reserved zones
 * are irregular die-cut outlines. So instead of computing them, we take the
 * exact SHAPES straight from named layers already present in the template JSON
 * (`Safe_Area`, `Bleed_Area`, `Fold_Lines`, `Text_Guides`, `Reserved…`) and
 * re-render them in the editor's standard guide STYLE.
 *
 * The transform:
 *  - Removes the raw named layers from user content (they must never be
 *    selectable, exported, or listed in the Layers panel).
 *  - Emits styled replacements marked `custom.guide === true`, so the existing
 *    template-guide machinery strips them at save and re-injects them on load
 *    (see labelEditor.service overlayTemplateGuides / isTemplateGuideElement).
 *  - `selectable: false` is what actually hides an element from Polotno's Layers
 *    panel (layers-panel.js: `display: selectable ? 'auto' : 'none'`).
 *
 * Pure JSON→JSON + SVG string rewriting, no browser/Polotno deps, so it can run
 * server-side (API) and be unit tested.
 */

// ── Layer names (the AI/PDF export convention shared by RST templates) ─────────

export const RST_SAFE_AREA_LAYER = "Safe_Area"
export const RST_TRIM_LINE_LAYER = "Trim_Line"
export const RST_BLEED_AREA_LAYER = "Bleed_Area"
export const RST_FOLD_LINES_LAYER = "Fold_Lines"
export const RST_TEXT_GUIDES_LAYER = "Text_Guides"
export const RST_RESERVED_LAYER = "Reserved"
export const RST_RESERVED_VECTORS_LAYER = "Reserved_vectors"

const RST_GUIDE_LAYER_NAMES = new Set<string>([
  RST_SAFE_AREA_LAYER,
  RST_TRIM_LINE_LAYER,
  RST_BLEED_AREA_LAYER,
  RST_FOLD_LINES_LAYER,
  RST_TEXT_GUIDES_LAYER,
  RST_RESERVED_LAYER,
  RST_RESERVED_VECTORS_LAYER,
])

/** Id prefix for the styled guides, so they never collide with the raw source
 * layers they're derived from (which we keep, hidden, as the persistent source). */
const RST_GUIDE_ID_PREFIX = "rst-guide-"

/**
 * Fully-resolved style for the RST guides — colors plus stroke widths/dashes in
 * CANVAS PX (the caller has already applied any size scaling). The transform
 * converts px to each SVG's own units (viewBox-aware) per element. The web
 * `GUIDE_STYLE` is the single source; the web builds this and passes it in so
 * style changes are live (no server round-trip) and size-adaptive.
 */
export type RstGuideStyle = {
  safeArea: {
    dashColor: string
    backingColor: string
    strokeWidth: number
    dash: number[]
  }
  trimLine: { color: string; strokeWidth: number }
  foldLine: {
    dashColor: string
    backingColor: string
    strokeWidth: number
    dash: number[]
  }
  bleed: { tint: string; opacity: number }
  matrixZone: {
    fill: string
    fillOpacity: number
    stroke: string
    strokeWidth: number
  }
  textGuide: { fill: string; opacity: number }
}

/**
 * Per-guide-type style overrides as authored in Sanity (the labelSize
 * `guideStyle` object). Every field is optional — the web merges whatever is set
 * over the built-in `GUIDE_STYLE` defaults, so a label size can tweak just one
 * color and leave the rest. Dashes are a length + gap pair here; the web joins
 * them into the `[dash, gap]` array `RstGuideStyle` expects. Shared so the API
 * (which reads it from Sanity) and the web (which resolves it) agree on shape.
 */
export type LabelGuideStyleConfig = {
  safeArea?: {
    dashColor?: string
    backingColor?: string
    strokeWidth?: number
    dashLength?: number
    dashGap?: number
  } | null
  trimLine?: { color?: string; strokeWidth?: number } | null
  foldLine?: {
    dashColor?: string
    backingColor?: string
    strokeWidth?: number
    dashLength?: number
    dashGap?: number
  } | null
  textGuide?: { fill?: string; opacity?: number } | null
  bleed?: { tint?: string; opacity?: number } | null
  matrixZone?: {
    fill?: string
    fillOpacity?: number
    stroke?: string
    strokeWidth?: number
  } | null
}

// Guide z-order (append order among alwaysOnTop guides — later renders higher):
// bleed washes lowest, then text zones, then the safe/trim/fold lines, matrix on
// top.
const GUIDE_TYPE_ORDER: Record<string, number> = {
  bleed: 0,
  text: 1,
  "safe-backing": 2,
  safe: 3,
  trim: 4,
  "fold-backing": 5,
  fold: 6,
  matrix: 7,
}

/**
 * Legend categories for template guides. Each groups the raw `guideType`s the
 * transform emits (e.g. a safe area is a backing + dashed pair) under one
 * user-facing toggle, so the RST legend lists each guide type separately —
 * matching how computed-guide SKUs show Bleed / Safe / Trim / Matrix. Ordered
 * bottom-of-stack → top for a stable legend order.
 */
export const TEMPLATE_GUIDE_CATEGORIES = [
  { key: "bleed", label: "Bleed Area", guideTypes: ["bleed"] },
  { key: "safe", label: "Safe Area", guideTypes: ["safe", "safe-backing"] },
  { key: "trim", label: "Trim Line", guideTypes: ["trim"] },
  { key: "fold", label: "Fold Lines", guideTypes: ["fold", "fold-backing"] },
  { key: "text", label: "Text Guides", guideTypes: ["text"] },
  { key: "matrix", label: "Reserved Space", guideTypes: ["matrix"] },
] as const

export type TemplateGuideCategoryKey =
  (typeof TEMPLATE_GUIDE_CATEGORIES)[number]["key"]

/** Fallback category for a template guide whose `guideType` isn't recognized. */
export const TEMPLATE_GUIDE_OTHER_CATEGORY = "other"

const GUIDE_TYPE_TO_CATEGORY = new Map<string, string>(
  TEMPLATE_GUIDE_CATEGORIES.flatMap((c) =>
    c.guideTypes.map((t) => [t, c.key] as const),
  ),
)

/**
 * The legend category key for a template-guide element (reads `custom.guideType`),
 * or `null` when the element is not a template guide. Unrecognized guide types
 * fall back to {@link TEMPLATE_GUIDE_OTHER_CATEGORY}.
 */
export function templateGuideCategoryOf(child: unknown): string | null {
  const rec = child as { custom?: unknown } | null
  const custom =
    rec && typeof rec === "object" && !Array.isArray(rec)
      ? (rec.custom as { guide?: unknown; guideType?: unknown } | undefined)
      : undefined
  if (!custom || custom.guide !== true) return null
  const guideType = typeof custom.guideType === "string" ? custom.guideType : ""
  return GUIDE_TYPE_TO_CATEGORY.get(guideType) ?? TEMPLATE_GUIDE_OTHER_CATEGORY
}

// ── Minimal element shape (kept loose — mirrors Prisma.JsonValue / PolotnoStore) ─

type GuideElement = Record<string, unknown> & {
  id?: string
  type?: string
  name?: string
  src?: string
  x?: number
  y?: number
  width?: number
  height?: number
}

type GuidePage = Record<string, unknown> & { children?: unknown[] }
type GuideJson = Record<string, unknown> & { pages?: unknown[] }

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** True for a raw named guide layer (pre-transform) in the template. */
function isRstGuideLayer(child: unknown): boolean {
  const rec = asRecord(child)
  return (
    rec !== null &&
    typeof rec.name === "string" &&
    RST_GUIDE_LAYER_NAMES.has(rec.name)
  )
}

/** Public alias — the raw named guide layer that acts as the shape source. Web
 * uses this to exclude the (hidden) source layers from recolor / restore / etc. */
export function isRstGuideLayerElement(child: unknown): boolean {
  return isRstGuideLayer(child)
}

/** True for an existing template guide (`custom.guide === true`). */
function isExistingTemplateGuide(child: unknown): boolean {
  const custom = asRecord((child as { custom?: unknown } | null)?.custom)
  return custom?.guide === true
}

/** True when a store JSON carries the RST named guide layers (any page). */
export function hasRstGuideLayers(json: unknown): boolean {
  const root = asRecord(json)
  if (!root || !Array.isArray(root.pages)) return false
  return root.pages.some((page) => {
    const rec = asRecord(page)
    const children = rec?.children
    return Array.isArray(children) && children.some(isRstGuideLayer)
  })
}

/**
 * Normalize a standalone "guides JSON" payload (a label size's shared guide
 * source, authored in Sanity as "just the contents of the GUIDES layer") into a
 * store-shaped `{ pages: [{ children }] }` that the API can consume. Accepts
 * the shapes an operator might paste:
 *  - a full store `{ pages: [...] }` (used as-is, preserving page structure);
 *  - a page-like `{ children: [...] }` or a wrapping group with `children`;
 *  - a bare array of guide layer elements;
 *  - a single named guide layer object.
 * Returns `null` when the payload carries no recognized RST guide layers, so the
 * caller can fall back to per-SKU extraction.
 */
export function coerceRstGuideSource(
  parsed: unknown,
): Record<string, unknown> | null {
  let candidate: GuideJson | null = null
  if (Array.isArray(parsed)) {
    candidate = { pages: [{ children: parsed }] }
  } else {
    const rec = asRecord(parsed) as GuideJson | null
    if (rec) {
      if (Array.isArray(rec.pages)) {
        candidate = rec
      } else if (Array.isArray((rec as GuidePage).children)) {
        candidate = { pages: [{ children: (rec as GuidePage).children }] }
      } else if (typeof (rec as GuideElement).name === "string") {
        candidate = { pages: [{ children: [rec] }] }
      }
    }
  }
  return candidate && hasRstGuideLayers(candidate) ? candidate : null
}

// ── SVG restyling (viewBox-aware so px widths stay visually consistent) ────────

// viewBox numbers may be separated by whitespace and/or commas (both spec-valid).
const VIEWBOX_RE =
  /viewBox\s*=\s*"([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)[\s,]+([-\d.eE]+)"/

/** Ratio of SVG user units to canvas px (px * scale = SVG units). Falls back to
 * 1 when the viewBox or element width is unusable. */
function unitScale(svgText: string, elementWidth: number | undefined): number {
  const m = svgText.match(VIEWBOX_RE)
  const vbW = m ? Number(m[3]) : NaN
  if (
    !Number.isFinite(vbW) ||
    vbW <= 0 ||
    typeof elementWidth !== "number" ||
    !Number.isFinite(elementWidth) ||
    elementWidth <= 0
  ) {
    return 1
  }
  return vbW / elementWidth
}

type PathStyle = {
  fill: string
  fillOpacity?: number
  stroke: string
  strokeWidthUnits?: number
  dashUnits?: number[]
}

/** Strip these presentation attributes so our style fully owns each path. */
function stripStyleAttrs(pathTag: string): string {
  return pathTag
    .replace(/\s+fill="[^"]*"/g, "")
    .replace(/\s+fill-opacity="[^"]*"/g, "")
    .replace(/\s+stroke="[^"]*"/g, "")
    .replace(/\s+stroke-width="[^"]*"/g, "")
    .replace(/\s+stroke-dasharray="[^"]*"/g, "")
    .replace(/\s+stroke-linecap="[^"]*"/g, "")
    .replace(/\s+stroke-linejoin="[^"]*"/g, "")
}

function styleAttrs(style: PathStyle): string {
  const parts = [`fill="${style.fill}"`]
  if (style.fillOpacity !== undefined) {
    parts.push(`fill-opacity="${style.fillOpacity}"`)
  }
  parts.push(`stroke="${style.stroke}"`)
  if (style.strokeWidthUnits !== undefined) {
    parts.push(`stroke-width="${style.strokeWidthUnits}"`)
    parts.push(`stroke-linecap="round"`, `stroke-linejoin="round"`)
  }
  if (style.dashUnits && style.dashUnits.length > 0) {
    parts.push(`stroke-dasharray="${style.dashUnits.join(",")}"`)
  }
  return parts.join(" ")
}

/** Rewrite every <path> in the SVG to use exactly `style`. */
function restyleSvg(svgText: string, style: PathStyle): string {
  const attrs = styleAttrs(style)
  return svgText.replace(/<path\b([^>]*?)(\/?)>/g, (_full, body, selfClose) => {
    const cleaned = stripStyleAttrs(body)
    return `<path ${attrs}${cleaned}${selfClose}>`
  })
}

/**
 * Force the SVG to rasterize at its on-canvas pixel size instead of its small
 * viewBox intrinsic size. RST source SVGs carry only a `viewBox` (no width/
 * height), so the browser rasterizes them tiny and Polotno magnifies that raster
 * ~4× to the element box — blurring thin strokes and making line widths / dash
 * gaps look inconsistent. Setting width/height to the element's px size makes the
 * raster full-resolution and the strokes uniform (mirrors the AAV trim-mask SVG).
 */
function setSvgSize(
  svgText: string,
  width: number | undefined,
  height: number | undefined,
): string {
  if (
    typeof width !== "number" ||
    typeof height !== "number" ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return svgText
  }
  return svgText.replace(/<svg\b([^>]*?)>/, (_full, attrs) => {
    const cleaned = attrs.replace(/\s+(?:width|height)="[^"]*"/g, "")
    return `<svg${cleaned} width="${width}" height="${height}">`
  })
}

// ── base64 data URI (Node + browser) ──────────────────────────────────────────

function decodeDataUri(src: unknown): string | null {
  if (typeof src !== "string") return null
  const marker = "base64,"
  const idx = src.indexOf(marker)
  if (idx < 0) return null
  const b64 = src.slice(idx + marker.length)
  const g = globalThis as {
    atob?: (s: string) => string
    Buffer?: {
      from: (s: string, e: string) => { toString: (e: string) => string }
    }
  }
  try {
    if (g.Buffer) return g.Buffer.from(b64, "base64").toString("utf8")
    if (g.atob) return decodeURIComponent(escape(g.atob(b64)))
  } catch {
    return null
  }
  return null
}

function encodeDataUri(svg: string): string {
  const g = globalThis as {
    btoa?: (s: string) => string
    Buffer?: {
      from: (s: string, e: string) => { toString: (e: string) => string }
    }
  }
  const b64 = g.Buffer
    ? g.Buffer.from(svg, "utf8").toString("base64")
    : g.btoa
      ? g.btoa(unescape(encodeURIComponent(svg)))
      : ""
  return `data:image/svg+xml;base64,${b64}`
}

// ── element builders ──────────────────────────────────────────────────────────

/** Common flags that make an element a locked, unlisted, non-printing guide. */
function guideFlags(guideType: string, name: string) {
  return {
    selectable: false,
    draggable: false,
    contentEditable: false,
    styleEditable: false,
    resizable: false,
    removable: false,
    showInExport: false,
    alwaysOnTop: true,
    name,
    custom: { guide: true, guideType },
  }
}

/** Geometry + identity carried over from the source layer. */
function baseGeometry(src: GuideElement): Record<string, unknown> {
  return {
    x: src.x ?? 0,
    y: src.y ?? 0,
    width: src.width,
    height: src.height,
    rotation: (src as { rotation?: number }).rotation ?? 0,
  }
}

/** A restyled SVG guide element from a source svg layer. */
function svgGuide(
  src: GuideElement,
  svgText: string,
  idSuffix: string,
  guideType: string,
  name: string,
  style: PathStyle,
  opacity = 1,
): GuideElement {
  return {
    ...baseGeometry(src),
    // Prefixed so the styled guide never collides with the (kept, hidden) raw
    // source layer's own id.
    id: `${RST_GUIDE_ID_PREFIX}${src.id ?? "rst"}${idSuffix}`,
    type: "svg",
    // setSvgSize forces a full-resolution raster so strokes/dashes stay uniform.
    src: encodeDataUri(
      setSvgSize(restyleSvg(svgText, style), src.width, src.height),
    ),
    opacity,
    ...guideFlags(guideType, name),
  }
}

/** Restyle one child of a Trim_Line group (an svg segment) as a solid trim line. */
function styleTrimSvgChild(
  child: GuideElement,
  style: RstGuideStyle,
): GuideElement | null {
  const svgText = decodeDataUri(child.src)
  if (!svgText) return null
  const scale = unitScale(svgText, child.width)
  return {
    ...child,
    src: encodeDataUri(
      setSvgSize(
        restyleSvg(svgText, {
          fill: "none",
          stroke: style.trimLine.color,
          strokeWidthUnits: style.trimLine.strokeWidth * scale,
        }),
        child.width,
        child.height,
      ),
    ),
    opacity: 1,
    selectable: false,
    draggable: false,
    showInExport: false,
  }
}

/** True for a near-white color (rgb/rgba/hex) — the fold-line backing convention.
 * Perceived luminance so any light backing reads as "backing", dark as "line". */
function isLightColor(color: unknown): boolean {
  if (typeof color !== "string") return false
  let r: number, g: number, b: number
  const rgb = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i)
  if (rgb) {
    r = Number(rgb[1])
    g = Number(rgb[2])
    b = Number(rgb[3])
  } else {
    const hex = color.replace("#", "")
    if (hex.length !== 6) return false
    r = parseInt(hex.slice(0, 2), 16)
    g = parseInt(hex.slice(2, 4), 16)
    b = parseInt(hex.slice(4, 6), 16)
  }
  if (![r, g, b].every(Number.isFinite)) return false
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.7
}

/** Restyle one child of a Fold_Lines group (a native line). RST templates draw
 * each fold as a light (white) BACKING line plus a dark line, ordered
 * backing-first — the same solid-backing + dashed-line pattern as the safe area.
 * So light source lines become the solid backing (no dash) and dark ones the
 * dashed line; styling both as dashed would double the dashes and look jagged.
 * Native lines render crisp (no SVG rasterization); a Polotno line's `height` is
 * its thickness and `dash` is px along its length. */
function styleFoldLineChild(
  child: GuideElement,
  style: RstGuideStyle,
): GuideElement {
  const isBacking = isLightColor(child.color)
  return {
    ...child,
    color: isBacking ? style.foldLine.backingColor : style.foldLine.dashColor,
    dash: isBacking ? [] : [...style.foldLine.dash],
    height: style.foldLine.strokeWidth,
    opacity: 1,
    selectable: false,
    draggable: false,
    showInExport: false,
  }
}

/**
 * Transform a grouped guide layer (RST templates now ship Fold_Lines as a group
 * of native lines and Trim_Line as a group of svg segments). The group is kept
 * intact — only its children are restyled — so any group transform is preserved;
 * the group carries the `custom.guide` marker + guideType for the legend/strip.
 */
function transformGroupLayer(
  group: GuideElement,
  name: string,
  style: RstGuideStyle,
): GuideElement[] {
  const children = Array.isArray((group as { children?: unknown }).children)
    ? ((group as { children: unknown[] }).children as GuideElement[])
    : []

  let guideType: string
  let label: string
  let styledChildren: GuideElement[]
  switch (name) {
    case RST_FOLD_LINES_LAYER:
      guideType = "fold"
      label = "Fold Lines"
      styledChildren = children.map((c) => styleFoldLineChild(c, style))
      break
    case RST_TRIM_LINE_LAYER:
      guideType = "trim"
      label = "Trim Line"
      styledChildren = children
        .map((c) => styleTrimSvgChild(c, style))
        .filter((c): c is GuideElement => c !== null)
      break
    default:
      return []
  }

  return [
    {
      ...group,
      id: `${RST_GUIDE_ID_PREFIX}${group.id ?? "group"}`,
      children: styledChildren,
      // Force visible: the source group was stamped opacity:0 by asHiddenSource.
      opacity: 1,
      ...guideFlags(guideType, label),
    },
  ]
}

/**
 * Transform one raw named layer into one or more styled guide elements, using
 * the resolved `style`. Returns [] when the layer can't be read (dropped).
 */
function transformLayer(
  layer: GuideElement,
  style: RstGuideStyle,
): GuideElement[] {
  const name = typeof layer.name === "string" ? layer.name : ""

  // Grouped guide layers (Fold_Lines / Trim_Line) — restyle children in place.
  if (layer.type === "group") {
    return transformGroupLayer(layer, name, style)
  }

  // Reserved caption text — keep as text, just lock + recolor to the matrix hue.
  if (name === RST_RESERVED_LAYER && layer.type === "text") {
    return [
      {
        ...layer,
        id: `${RST_GUIDE_ID_PREFIX}${layer.id ?? "reserved"}`,
        fill: style.matrixZone.stroke,
        // Force visible: the source layer we spread from was stamped opacity:0 by
        // asHiddenSource, so without this the styled caption inherits it and
        // renders invisible.
        opacity: 1,
        // Name must NOT match a raw layer name (`Reserved`) or the styled guide
        // would be misdetected as a source layer by isRstGuideLayerElement.
        ...guideFlags("matrix", "Reserved Space"),
      },
    ]
  }

  const svgText = decodeDataUri(layer.src)
  if (!svgText) return []
  const scale = unitScale(svgText, layer.width)
  // Style widths/dashes are canvas px → convert to this SVG's own units.
  const sw = (px: number) => px * scale
  const dash = (arr: readonly number[]) => arr.map((d) => d * scale)

  switch (name) {
    case RST_SAFE_AREA_LAYER: {
      const s = style.safeArea
      return [
        svgGuide(layer, svgText, "-backing", "safe-backing", "Safe Area", {
          fill: "none",
          stroke: s.backingColor,
          strokeWidthUnits: sw(s.strokeWidth),
        }),
        svgGuide(layer, svgText, "", "safe", "Safe Area", {
          fill: "none",
          stroke: s.dashColor,
          strokeWidthUnits: sw(s.strokeWidth),
          dashUnits: dash(s.dash),
        }),
      ]
    }
    case RST_TRIM_LINE_LAYER: {
      // The die-cut boundary — a single solid line (no dash, no backing), like
      // the AAV computed trim line.
      const t = style.trimLine
      return [
        svgGuide(layer, svgText, "", "trim", "Trim Line", {
          fill: "none",
          stroke: t.color,
          strokeWidthUnits: sw(t.strokeWidth),
        }),
      ]
    }
    case RST_FOLD_LINES_LAYER: {
      const f = style.foldLine
      return [
        svgGuide(layer, svgText, "-backing", "fold-backing", "Fold Lines", {
          fill: "none",
          stroke: f.backingColor,
          strokeWidthUnits: sw(f.strokeWidth),
        }),
        svgGuide(layer, svgText, "", "fold", "Fold Lines", {
          fill: "none",
          stroke: f.dashColor,
          strokeWidthUnits: sw(f.strokeWidth),
          dashUnits: dash(f.dash),
        }),
      ]
    }
    case RST_BLEED_AREA_LAYER: {
      const b = style.bleed
      // Two bleed layers overlap by design; each is its own tinted wash.
      return [
        svgGuide(
          layer,
          svgText,
          "",
          "bleed",
          "Bleed Area",
          { fill: b.tint, stroke: "none" },
          b.opacity,
        ),
      ]
    }
    case RST_TEXT_GUIDES_LAYER: {
      const t = style.textGuide
      return [
        svgGuide(
          layer,
          svgText,
          "",
          "text",
          "Text Guides",
          { fill: t.fill, stroke: "none" },
          t.opacity,
        ),
      ]
    }
    case RST_RESERVED_VECTORS_LAYER: {
      const m = style.matrixZone
      return [
        svgGuide(layer, svgText, "", "matrix", "Matrix Code Zone", {
          fill: m.fill,
          fillOpacity: m.fillOpacity,
          stroke: m.stroke,
          strokeWidthUnits: sw(m.strokeWidth),
        }),
      ]
    }
    default:
      return []
  }
}

/** Per-page styled guide elements, sorted into their guide z-order. */
function buildPageGuides(
  children: unknown[],
  style: RstGuideStyle,
): GuideElement[] {
  const guides = children
    .filter(isRstGuideLayer)
    .flatMap((layer) => transformLayer(layer as GuideElement, style))
  return guides.sort((a, b) => {
    const ra = GUIDE_TYPE_ORDER[guideTypeOf(a)] ?? 0
    const rb = GUIDE_TYPE_ORDER[guideTypeOf(b)] ?? 0
    return ra - rb
  })
}

function guideTypeOf(el: GuideElement): string {
  const custom = asRecord((el as { custom?: unknown }).custom)
  return typeof custom?.guideType === "string" ? custom.guideType : ""
}

// ── public API ────────────────────────────────────────────────────────────────

/** A styled guide this module produced (id-prefixed + `custom.guide`). */
function isRstStyledGuide(child: unknown): boolean {
  const rec = asRecord(child)
  return (
    rec !== null &&
    typeof rec.id === "string" &&
    rec.id.startsWith(RST_GUIDE_ID_PREFIX) &&
    isExistingTemplateGuide(child)
  )
}

/** Mark a raw named layer as a hidden, locked, non-exported shape source.
 * Exported: the API's withRstGuideSource stamps grafted source layers with the
 * same markers this module's overlay derivation relies on. */
export function asHiddenSource(child: unknown): unknown {
  if (!isRstGuideLayer(child)) return child
  return {
    ...(child as GuideElement),
    opacity: 0,
    selectable: false,
    draggable: false,
    showInExport: false,
  }
}

/**
 * Build the styled `custom.guide` overlay from the raw named layers already in
 * `json`, using the resolved `style`. The raw layers are KEPT as a hidden source
 * (so autosave — which strips only `custom.guide` — can't destroy them and the
 * overlay re-derives on the next load). Idempotent: any prior styled guides are
 * replaced. Runs client-side, so `GUIDE_STYLE` edits are live and size-adaptive.
 */
export function injectRstStyledGuides<T>(json: T, style: RstGuideStyle): T {
  const root = asRecord(json) as GuideJson | null
  if (!root || !Array.isArray(root.pages)) return json
  return {
    ...root,
    pages: root.pages.map((page) => {
      const rec = asRecord(page) as GuidePage | null
      if (!rec || !Array.isArray(rec.children)) return page
      // Keep content + hidden source (drop only our prior styled guides).
      const content = rec.children
        .filter((c) => !isRstStyledGuide(c))
        .map(asHiddenSource)
      const guides = buildPageGuides(rec.children, style)
      return { ...rec, children: [...content, ...guides] }
    }),
  } as T
}
